// Section 19: Blight. 5% per season, Summer bias. The year's harvests (to the next Autumn) are
// 0.3x and seed saved from them is worth 0.5x. Choices: Burn the affected fields (lose 30% of
// the crop now; next year normal) or Harvest anyway (0.3x, seed 0.5x, and the blight returns
// next year half the time). Constants: config.events.blight.
import type { Config } from '../../config';
import {
  choiceOutcome,
  choiceRng,
  effectDelta,
  effectParamModifiers,
  eventEntry,
  eventPrompt,
  seasonOfTurn,
} from '../../core/content-helpers';
import { defineEvent, type EventChoiceDef } from '../../core/registry';
import type { Delta, ReadContext } from '../../core/types';

/** Seasons from `from` through the next Autumn harvest (Section 8), at least 1. */
function toHarvest(ctx: ReadContext, from: number): number {
  const n = ctx.cfg.seasons.order.length;
  for (let i = 0; i < n; i++) if (seasonOfTurn(ctx, from + i) === 'autumn') return i + 1;
  return 1;
}

export function blight(config: Config) {
  const c = config.events.blight;
  const pct = (x: number): number => Math.round(x * 100);
  const choices: EventChoiceDef[] = [
    {
      id: 'burn-fields',
      label: 'Burn the affected fields',
      preview: () => [{ key: 'blight-burn', params: { loss: pct(c.burnLoss) } }],
      apply: (ctx, prompt) => [
        effectDelta('blight', 'burned', ctx.season.turn, toHarvest(ctx, ctx.season.turn), {
          harvestMul: 1 - c.burnLoss,
        }),
        choiceOutcome(prompt, 'burn-fields', { loss: pct(c.burnLoss) }),
      ],
    },
    {
      id: 'harvest-anyway',
      label: 'Harvest anyway',
      preview: () => [
        {
          key: 'blight-harvest',
          params: { mul: c.harvestMul, seed: c.seedMul, persist: pct(c.persistChance) },
        },
      ],
      apply: (ctx, prompt) => {
        const from = ctx.season.turn;
        const seasons = toHarvest(ctx, from);
        const params = { harvestMul: c.harvestMul, seedMul: c.seedMul };
        const out: Delta[] = [effectDelta('blight', 'blighted', from, seasons, params)];
        if (choiceRng(prompt, 'persist').float() < c.persistChance) {
          const next = from + ctx.cfg.people.seasonsPerYear;
          out.push(effectDelta('blight', 'returns', next, seasons, { ...params, returns: 1 }));
        }
        out.push(choiceOutcome(prompt, 'harvest-anyway', { mul: c.harvestMul }));
        return out;
      },
    },
  ];
  return defineEvent({
    id: 'blight',
    label: 'Blight',
    section: 'S19',
    baseWeight: c.baseChance,
    seasons: ['summer'],
    foreshadow: null,
    ledgerTag: 'blight',
    weight: () => c.baseChance,
    text: () => eventEntry('blight', { mul: c.harvestMul }),
    apply: (ctx) => [eventPrompt(ctx, 'blight', choices, { defaultChoiceId: 'harvest-anyway' })],
    choices,
    modifierTargets: ['harvestYield'],
    modifiers: effectParamModifiers,
  });
}
